<template>
  <v-container fluid class="clinicInformation px-12 py-4">
    <v-row>
      <v-col cols="12">
        <breadcrumbs></breadcrumbs>
        <h1>Clinic Information</h1>
        <v-divider class="my-3"></v-divider>
      </v-col>
    </v-row>

    <h2 class="title">Locations</h2>
    <v-card outlined class="mt-2">
      <v-card-text>
        <v-data-table :headers="locationTableHeaders" :items="locationList">
          <template v-slot:item.addressId="{ item }">
            {{ formatAddress(item.addressId) }}
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <v-row class="mt-8">
      <v-col :cols="6">
        <h2 class="title">Pay-To Address</h2>
        <v-card outlined class="mt-2">
          <v-card-text>
            <div v-html="payToAddress"></div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col :cols="6">
        <h2 class="title">Contact Person</h2>
        <v-card outlined class="mt-2">
          <v-card-text>
            <table>
              <tr>
                <th>Name:</th>
                <td>
                  {{
                    organization.billingClaimsEnrollmentContactPerson || "--"
                  }}
                </td>
              </tr>
              <tr>
                <th>Phone:</th>
                <td>
                  {{ organization.billingClaimsEnrollmentContactPhone || "--" }}
                </td>
              </tr>
              <tr>
                <th>Email:</th>
                <td>
                  {{ organization.billingClaimsEnrollmentContactEmail || "--" }}
                </td>
              </tr>
            </table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-divider class="mt-6"></v-divider>
    <v-row class="mt-6">
      <v-col :cols="12">
        <h2 class="title">Encounter Rates</h2>
        <v-card outlined class="mt-2">
          <v-card-text>
            <table>
              <tr>
                <th>Effective Date:</th>
                <td>
                  {{
                    formatDate(
                      organization.billingClaimsEncounterRateEffectiveDate
                    ) || "--"
                  }}
                </td>
              </tr>
              <tr>
                <th>G0466:</th>
                <td>
                  {{
                    organization.billingClaimsEncounterRateG0466
                      ? `$${organization.billingClaimsEncounterRateG0466}`
                      : "--"
                  }}
                </td>
              </tr>
              <tr>
                <th>G0467:</th>
                <td>
                  {{
                    organization.billingClaimsEncounterRateG0467
                      ? `$${organization.billingClaimsEncounterRateG0467}`
                      : "--"
                  }}
                </td>
              </tr>
              <tr>
                <th>G0468:</th>
                <td>
                  {{
                    organization.billingClaimsEncounterRateG0468
                      ? `$${organization.billingClaimsEncounterRateG0468}`
                      : "--"
                  }}
                </td>
              </tr>
              <tr>
                <th>G0469:</th>
                <td>
                  {{
                    organization.billingClaimsEncounterRateG0469
                      ? `$${organization.billingClaimsEncounterRateG0469}`
                      : "--"
                  }}
                </td>
              </tr>
              <tr>
                <th>G0470:</th>
                <td>
                  {{
                    organization.billingClaimsEncounterRateG0470
                      ? `$${organization.billingClaimsEncounterRateG0470}`
                      : "--"
                  }}
                </td>
              </tr>
              <tr>
                <th>T1015:</th>
                <td>
                  {{
                    organization.billingClaimsEncounterRateT1015
                      ? `$${organization.billingClaimsEncounterRateT1015}`
                      : "--"
                  }}
                </td>
              </tr>
            </table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <div v-if="showAnyEncounterRates(locationList)">
      <v-divider class="mt-6"></v-divider>

      <h2 class="mt-6">California only - Medi-Cal Encounter Rates</h2>

      <div
        class="mt-3"
        v-for="location in locationList"
        :key="location.locationId"
      >
        <v-row class="mt-3" v-if="showEncounterRates(location.addressId)">
          <v-col :cols="12">
            <h3>{{ location.name }}</h3>
            <v-card outlined class="mt-2">
              <v-card-text>
                <table>
                  <tr>
                    <th>Effective Date</th>
                    <td>
                      {{
                        formatDate(location.mediCalEncounterRateEffectiveDate)
                      }}
                    </td>
                  </tr>
                  <tr>
                    <th>Medical (01):</th>
                    <td>
                      {{
                        location.mediCalEncounterRate01
                          ? `$${location.mediCalEncounterRate01}`
                          : "--"
                      }}
                    </td>
                  </tr>
                  <tr>
                    <th>Crossover (02):</th>
                    <td>
                      {{
                        location.mediCalEncounterRate02
                          ? `$${location.mediCalEncounterRate02}`
                          : "--"
                      }}
                    </td>
                  </tr>
                  <tr>
                    <th>Dental (03):</th>
                    <td>
                      {{
                        location.mediCalEncounterRate03
                          ? `$${location.mediCalEncounterRate03}`
                          : "--"
                      }}
                    </td>
                  </tr>
                  <tr>
                    <th>Optometry (04):</th>
                    <td>
                      {{
                        location.mediCalEncounterRate04
                          ? `$${location.mediCalEncounterRate04}`
                          : "--"
                      }}
                    </td>
                  </tr>
                  <tr>
                    <th>LCSW (11):</th>
                    <td>
                      {{
                        location.mediCalEncounterRate11
                          ? `$${location.mediCalEncounterRate11}`
                          : "--"
                      }}
                    </td>
                  </tr>
                  <tr>
                    <th>Psychologist (12):</th>
                    <td>
                      {{
                        location.mediCalEncounterRate12
                          ? `$${location.mediCalEncounterRate12}`
                          : "--"
                      }}
                    </td>
                  </tr>
                  <tr>
                    <th>Psychiatrist (13):</th>
                    <td>
                      {{
                        location.mediCalEncounterRate13
                          ? `$${location.mediCalEncounterRate13}`
                          : "--"
                      }}
                    </td>
                  </tr>
                  <tr>
                    <th>Acupuncture (15):</th>
                    <td>
                      {{
                        location.mediCalEncounterRate15
                          ? `$${location.mediCalEncounterRate15}`
                          : "--"
                      }}
                    </td>
                  </tr>
                  <tr>
                    <th>Chiropractic (16):</th>
                    <td>
                      {{
                        location.mediCalEncounterRate16
                          ? `$${location.mediCalEncounterRate16}`
                          : "--"
                      }}
                    </td>
                  </tr>
                  <tr>
                    <th>Managed Care Differential (18):</th>
                    <td>
                      {{
                        location.mediCalEncounterRate18
                          ? `$${location.mediCalEncounterRate18}`
                          : "--"
                      }}
                    </td>
                  </tr>
                  <tr>
                    <th>Capitated Medicare Adv (20):</th>
                    <td>
                      {{
                        location.mediCalEncounterRate20
                          ? `$${location.mediCalEncounterRate20}`
                          : "--"
                      }}
                    </td>
                  </tr>
                </table>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </div>
    </div>
  </v-container>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import cloneDeep from "lodash/cloneDeep";
import moment from "moment";
import Breadcrumbs from "@/views/Breadcrumbs";

export default {
  components: {
    Breadcrumbs,
  },
  computed: {
    ...mapGetters({
      getAddress: "address/getById",
    }),
    ...mapState({
      addressRecord: state => state.address.record,
      locationList: state => state.location.list,
      organizationRecord: state => state.organization.record,
      organizationId: state => state.session.organizationId,
    }),
    isFqhc() {
      return this.organizationRecord.designation.includes(
        "Federally Qualified Health Center"
      );
    },
    locationCount() {
      if (this.locationList) {
        return this.locationList.length;
      }
      return "--";
    },
    payToAddress() {
      if (!this.organizationRecord) return "--";
      return this.formatAddress(
        this.organizationRecord.billingClaimsPayToAddress,
        true
      );
    },
  },
  async created() {
    await this.getOrganization(this.organizationId);
    this.organization = cloneDeep(this.organizationRecord);
    await this.getAddressList();
    await this.getLocationList();
  },
  data() {
    return {
      addressList: [],
      locationTableHeaders: [
        {
          text: "Clinic Name",
          value: "name",
          align: "left",
        },
        {
          text: "Address",
          value: "addressId",
          align: "left",
        },
        {
          text: "Phone",
          value: "locationPhone",
          align: "left",
        },
        {
          text: "Fax",
          value: "locationFax",
          align: "left",
        },
        {
          text: "Tax ID",
          value: "taxId",
          align: "left",
        },
        {
          text: "NPI",
          value: "npi",
          align: "left",
        },
        {
          text: "Taxonomy Code",
          value: "taxonomyCode",
          align: "left",
        },
        {
          text: "CLIA #",
          value: "cliaNumber",
          align: "left",
        },
      ],
      mainAddress: {},
      organization: {},
    };
  },
  methods: {
    ...mapActions({
      getAddressList: "address/list",
      getLocationList: "location/list",
      getOrganization: "organization/getByOrganizationId",
    }),
    formatAddress(addressId, blockFormat) {
      const addressRecord = this.getAddress(addressId);
      if (!addressRecord) return "--";
      const { address, address2, city, state, zip } = addressRecord;

      if (blockFormat) {
        return `${address}${
          address2 ? "<br>" + address2 : ""
        }<br>${city}, ${state} ${zip}`;
      }
      return `${address}, ${
        address2 ? address2 + ", " : ""
      }${city}, ${state} ${zip}`;
    },
    formatDate(date) {
      if (!date) {
        return "--";
      }
      return moment.utc(date).format("MMMM DD, YYYY");
    },
    showAnyEncounterRates(locationList) {
      return !!locationList.find(location => {
        return this.showEncounterRates(location.addressId);
      });
    },
    showEncounterRates(addressId) {
      const addressRecord = this.getAddress(addressId);
      if (!addressRecord) return false;
      const { state } = addressRecord;

      if (state === "CA" && this.isFqhc) {
        return true;
      }
      return false;
    },
    isEnabled(slot) {
      return this.enabled === slot;
    },
  },
};
</script>

<style>
.clinicInformation th {
  text-align: right !important;
}
.clinicInformation td {
  padding-left: 15px !important;
}
</style>
